"use client";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import {
    EventBySlugOutput,
    GetAllEventsOutput,
} from "~/types/EventTypesInferred";
import { UserFull } from "~/types/UserTypes";
import { api } from "~/utils/api";

export type UseUserPermissionsReturn = {
    isAdmin: boolean;
    isLoading: boolean;
    isManager: boolean;
    isHost: boolean;
    isPreapprovedHost: boolean;

    user: UserFull;
    getPermissions: (
        user: UserFull,
        event: InputEventType,
    ) => { isManager: boolean; isHost: boolean };
};

export enum EventType {
    Any = 1,
}

type InputEventType = EventBySlugOutput | EventType | GetAllEventsOutput;

function useUserPemissions({
    event,
}: {
    event?: InputEventType;
}): UseUserPermissionsReturn {
    const { data: sessionData } = useSession();
    const isAdmin = sessionData?.user.role === "ADMIN";

    const [isManager, setIsManager] = useState<boolean>(false);
    const [isHost, setIsHost] = useState<boolean>(false);
    const [isPreapprovedHost, setIsPreapprovedHost] = useState<boolean>(false);

    // check in session user object if we need to perform
    // server checks (isManger, isHost)
    const enabled =
        !!sessionData?.user?.id &&
        (sessionData?.user?.isManager || sessionData?.user?.isHost);

    const { data: user } = api.user.getUserById.useQuery(
        { userId: sessionData?.user?.id as string },
        { enabled },
    );

    const [isLoading, setIsLoading] = useState<boolean>(enabled);

    const getPermissions = (user: UserFull, event: InputEventType) => {
        const perms = {
            isManager: false,
            isHost: false,
            isPreapprovedHost: false,
        };
        if (!user || !event) return perms;

        if (event === EventType.Any) {
            perms.isManager = !!user.managedCommunities.length;
        } else {
            perms.isManager = user.managedCommunities.some(
                (c) => c.id === event.community?.id,
            );
            perms.isPreapprovedHost = user.preapproval.some(
                (p) => p.communityId === event.community?.id,
            );
            perms.isHost = event.hosts.some((h) => h.user?.id === user.id);
        }
        return perms;
    };

    useEffect(() => {
        if (!user || !event) return;
        const { isManager, isHost, isPreapprovedHost } = getPermissions(
            user,
            event,
        );
        setIsManager(isManager);
        setIsHost(isHost);
        setIsPreapprovedHost(isPreapprovedHost);
        setIsLoading(false);
    }, [event, user]);

    return {
        isAdmin,
        isManager,
        isHost,
        isPreapprovedHost,
        isLoading,

        // @ts-expect-error
        user,
        getPermissions,
    };
}

export default useUserPemissions;
